import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/auth/hooks';
import { LoginForm } from './components/LoginForm';
import toast from 'react-hot-toast';
import { Keyboard } from '@capacitor/keyboard';
import { PrivacyConsent } from '../../components/PrivacyConsent';
import { hasLocalConsent } from '../../lib/policy';
import { Calendar } from 'lucide-react';

function Login() {
  const navigate = useNavigate();
  const { isCreatingAccount, signInWithGoogle } = useAuth();

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [policyAccepted, setPolicyAccepted] = useState<boolean>(hasLocalConsent());

  const [kbOpen, setKbOpen] = useState(false);
  const [kbHeight, setKbHeight] = useState(0);

  const isNative = useMemo(() => {
    try {
      return !!window?.Capacitor?.isNativePlatform?.();
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {

    let vvHandler: ((e: any) => void) | null = null;
    let willShowHandle: { remove: () => Promise<void> } | null = null;
    let willHideHandle: { remove: () => Promise<void> } | null = null;

    (async () => {
      try {
        if (isNative) {
          try {
            await Keyboard.setResizeMode({ mode: 'none' });
          } catch {}

          willShowHandle = await Keyboard.addListener('keyboardWillShow', (info) => {
            setKbOpen(true);
            setKbHeight(info.keyboardHeight ?? 0);
            document.documentElement.style.overscrollBehaviorY = 'contain';
          });

          willHideHandle = await Keyboard.addListener('keyboardWillHide', () => {
            setKbOpen(false);
            setKbHeight(0);
            document.documentElement.style.overscrollBehaviorY = '';
          });
        } else if (window.visualViewport) {
          vvHandler = () => {
            const vv = window.visualViewport!;
            const heightLoss = window.innerHeight - vv.height;
            if (heightLoss > 120) {
              setKbOpen(true);
              setKbHeight(Math.round(heightLoss));
              document.documentElement.style.overscrollBehaviorY = 'contain';
            } else {
              setKbOpen(false);
              setKbHeight(0);
              document.documentElement.style.overscrollBehaviorY = '';
            }
          };
          window.visualViewport.addEventListener('resize', vvHandler);
        }
      } catch (e) {

      }
    })();

    return () => {
      if (willShowHandle?.remove) void willShowHandle.remove();
      if (willHideHandle?.remove) void willHideHandle.remove();
      if (vvHandler && window.visualViewport) {
        window.visualViewport.removeEventListener('resize', vvHandler);
      }
      document.documentElement.style.overscrollBehaviorY = '';
    };
  }, [isNative]);

  useEffect(() => {
    const url = new URL(window.location.href);
    const hasCode = url.searchParams.get('code');
    if (hasCode) {

    }
  }, []);

  const handleGoogleLogin = async () => {
    setLoading(true);

    try {
      await signInWithGoogle();
    } catch (e) {

      toast.error('שגיאה בהתחברות עם Google');
      setLoading(false);
    }
  };

  if (isCreatingAccount) return null;

  const containerPaddingBottom = kbOpen ? `${kbHeight + 16}px` : 'env(safe-area-inset-bottom)';

  return (
    <div
      className="min-h-[100dvh] flex items-center justify-center py-4 sm:py-8 px-3 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 via-white to-gray-50 overflow-auto"
      style={{ paddingBottom: containerPaddingBottom as any, WebkitOverflowScrolling: 'touch', overscrollBehaviorY: 'none' }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.3, type: "spring", stiffness: 100 }}
        className="w-full max-w-md space-y-4 sm:space-y-6 bg-white p-5 sm:p-8 md:p-10 rounded-2xl sm:rounded-3xl shadow-2xl"
      >
        <div className="text-center">
          <motion.div
            className="mx-auto h-16 w-16 sm:h-20 sm:w-20 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl sm:rounded-3xl flex items-center justify-center shadow-xl"
            initial={{ scale: 0.8, opacity: 0, rotate: 0 }}
            animate={{ scale: 1, opacity: 1, rotate: 360 }}
            whileHover={{ rotate: 720, scale: 1.1 }}
            transition={{
              scale: { delay: 0.1, type: "spring", stiffness: 200 },
              opacity: { delay: 0.1 },
              rotate: { duration: 0.8, ease: "easeInOut" }
            }}
          >
            <Calendar className="w-8 h-8 sm:w-10 sm:h-10 text-white" strokeWidth={2.5} />
          </motion.div>
          <motion.h2
            className="mt-4 sm:mt-6 text-2xl sm:text-3xl font-extrabold text-gray-900"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
          >
            ברוכים הבאים
          </motion.h2>
          <motion.h1
            className="mt-1 text-3xl sm:text-4xl font-black bg-gradient-to-r from-blue-600 to-blue-500 bg-clip-text text-transparent cursor-pointer hover:opacity-80 transition-opacity"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            onClick={() => navigate('/')}
            title="לדף הבית"
          >
            LoyalFlow
          </motion.h1>
          <motion.p
            className="mt-3 sm:mt-4 text-sm sm:text-base text-gray-600"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.25 }}
          >
            עדיין אין לך חשבון?{' '}
            <button
              onClick={() => navigate('/register')}
              className="font-bold text-blue-600 hover:text-blue-700 transition-colors underline-offset-2 hover:underline"
            >
              הרשמה
            </button>
          </motion.p>
        </div>

        <LoginForm
          email={formData.email}
          password={formData.password}
          loading={loading}
          onEmailChange={(value) => setFormData((prev) => ({ ...prev, email: value }))}
          onPasswordChange={(value) => setFormData((prev) => ({ ...prev, password: value }))}
          onSuccess={async () => {
            setLoading(false);
          }}
        />

        <div className="mt-1">
          <PrivacyConsent checked={policyAccepted} onChange={setPolicyAccepted} />
        </div>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-200"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-4 bg-white text-gray-500 font-medium">או</span>
          </div>
        </div>

        <motion.button
          onClick={handleGoogleLogin}
          disabled={loading}
          whileHover={{ scale: loading ? 1 : 1.02 }}
          whileTap={{ scale: loading ? 1 : 0.98 }}
          className="w-full flex items-center justify-center gap-2 sm:gap-3 py-3 sm:py-4 px-4 border-2 border-gray-200 rounded-2xl text-sm sm:text-base font-bold text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-300 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-lg"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" width="20" height="20" className="sm:w-6 sm:h-6" aria-hidden="true">
            <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12  s5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24  s8.955,20,20,20s20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"/>
            <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,16.098,18.961,13,24,13c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657  C34.046,6.053,29.268,4,24,4C16.318,4,9.66,8.337,6.306,14.691z"/>
            <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.197l-6.191-5.238C29.211,35.091,26.715,36,24,36  c-5.196,0-9.607-3.317-11.268-7.946l-6.524,5.025C9.518,39.556,16.227,44,24,44z"/>
            <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.793,2.239-2.231,4.166-3.994,5.565  c0.001-0.001,0.002-0.001,0.003-0.002l6.191,5.238C35.271,40.029,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"/>
          </svg>
          התחברות עם Google
        </motion.button>

        <motion.div
          className="flex justify-center mt-4 sm:mt-6"
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <button
            onClick={() => navigate('/forgot-password')}
            className="text-xs sm:text-sm font-semibold text-blue-600 hover:text-blue-700 transition-colors hover:underline underline-offset-2"
          >
            שכחת סיסמה?
          </button>
        </motion.div>
      </motion.div>
    </div>
  );
}

export default Login;
